import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyContext } from '../contexts/CurrencyContext';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useOnChainPortfolio } from '../hooks/useOnChainPortfolio';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { CalculatorFactory } from '../services/calculator';
import { StorageService } from '../services/storage';
import { Icons } from '../components/Icons';
import { FluxNodeStatusWidget } from '../components/dashboard/FluxNodeStatusWidget';
import { FluxAppsWidget } from '../components/dashboard/FluxAppsWidget';
import { CronosStakingWidget } from '../components/dashboard/CronosStakingWidget';
import { HederaStakingWidget } from '../components/dashboard/HederaStakingWidget';
import { TheGraphWidget } from '../components/dashboard/TheGraphWidget';
import { OnChainWalletSelector } from '../components/dashboard/OnChainWalletSelector';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../contexts/LanguageContext';

import { fmt } from '../utils/formatters';
import type { Prices, FIFOResults, Position, CalculationMethod } from '../types';
import './HomePage.css';

// Discrete Quiz Owl - just the mascot with hover animation
const QuizOwl = ({ onClick }: { onClick: () => void }) => {
  const messages = [
    "Teste-toi !",
    "Un petit quiz ?",
    "Tu viens jouer ?",
    "Prêt pour un défi ?",
  ];
  const [message] = useState(() => messages[Math.floor(Math.random() * messages.length)]);

  return (
    <div className="home-page__owl" onClick={onClick} title="Quiz Crypto">
      <div className="home-page__owl-bubble">{message}</div>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="home-page__owl-svg">
        <defs>
          <linearGradient id="owlGradientHome" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="eyeGlowHome" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        {/* Wings */}
        <ellipse className="owl-wing-left" cx="22" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />
        <ellipse className="owl-wing-right" cx="78" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />
        {/* Body */}
        <ellipse cx="50" cy="55" rx="32" ry="35" fill="url(#owlGradientHome)" />
        {/* Belly */}
        <ellipse cx="50" cy="62" rx="20" ry="22" fill="#c4b5fd" opacity="0.5" />
        {/* Face */}
        <circle cx="50" cy="40" r="28" fill="#ede9fe" />
        {/* Eye backgrounds */}
        <ellipse cx="38" cy="38" rx="12" ry="14" fill="url(#eyeGlowHome)" />
        <ellipse cx="62" cy="38" rx="12" ry="14" fill="url(#eyeGlowHome)" />
        {/* Eyes */}
        <ellipse className="owl-eye" cx="38" cy="38" rx="8" ry="10" fill="#1a1a2e" />
        <ellipse className="owl-eye" cx="62" cy="38" rx="8" ry="10" fill="#1a1a2e" />
        {/* Pupils */}
        <circle className="owl-pupil" cx="40" cy="38" r="3" fill="#fff" />
        <circle className="owl-pupil" cx="64" cy="38" r="3" fill="#fff" />
        {/* Beak */}
        <path d="M 45 48 L 50 58 L 55 48 Z" fill="#f59e0b" />
        {/* Ear tufts */}
        <path d="M 28 15 L 32 25 L 25 22 Z" fill="#8b5cf6" />
        <path d="M 72 15 L 68 25 L 75 22 Z" fill="#8b5cf6" />
        {/* Feet */}
        <ellipse cx="40" cy="88" rx="8" ry="4" fill="#f59e0b" />
        <ellipse cx="60" cy="88" rx="8" ry="4" fill="#f59e0b" />
        {/* Sparkles (on hover) */}
        <g className="owl-sparkle" style={{ transformOrigin: '15px 20px' }}>
          <path d="M 15 15 L 15 25 M 10 20 L 20 20" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>
        <g className="owl-sparkle" style={{ transformOrigin: '85px 25px' }}>
          <path d="M 85 20 L 85 30 M 80 25 L 90 25" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
};

// Blog article type (Supabase API)
interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image: string | null;
  reading_time: number;
  published_at: string | null;
  is_featured: boolean;
  category: {
    id: string;
    name: string;
    slug: string;
    color: string | null;
  } | null;
}

// Check if cover image is valid (absolute URL)
function hasValidCover(cover_image: string | null): boolean {
  return !!cover_image && cover_image.startsWith('http');
}

interface HomePageProps {
  calculationMethod?: CalculationMethod;
  onNavigate?: (pageId: string) => void;
}

// Get greeting key based on time of day
function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'home.greetingMorning';
  if (hour < 18) return 'home.greetingAfternoon';
  return 'home.greetingEvening';
}

export function HomePage({ calculationMethod = 'FIFO', onNavigate }: HomePageProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { currency, currencyInfo } = useCurrencyContext();
  const { user } = useAuthStore();
  const [aiMessageIndex, setAiMessageIndex] = useState(0);
  const [blogArticles, setBlogArticles] = useState<BlogArticle[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogImageErrors, setBlogImageErrors] = useState<Set<string>>(new Set());

  // Get display name: username if logged in, otherwise default
  const displayName = user?.username || t('home.cryptoInvestor');

  // Fetch blog articles from Supabase API with language support (random selection)
  useEffect(() => {
    async function fetchBlogArticles() {
      setBlogLoading(true);
      try {
        // Fetch a larger pool of articles to pick randomly from
        const res = await fetch(`/api/blog?action=articles&limit=30&lang=${language}`);
        const data = await res.json();
        const pool: BlogArticle[] = data.data || [];

        // Shuffle using Fisher-Yates and pick 3
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }

        setBlogArticles(pool.slice(0, 3));
      } catch (error) {
        console.error('Error fetching blog articles:', error);
      } finally {
        setBlogLoading(false);
      }
    }

    fetchBlogArticles();
  }, [language]);

  // Format blog date based on language
  const formatBlogDate = useCallback((dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  }, [language]);

  // Portfolio data
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  // OnChain Portfolio data
  const onChainHook = useOnChainPortfolio();

  // Calculate aggregated OnChain value (only included wallets)
  const onChainAggregated = useMemo(() => {
    const includedWallets = onChainHook.wallets.filter(w => w.include_in_homepage);
    const totalValue = includedWallets.reduce((sum, w) => sum + (w.total_value_usd || 0), 0);
    const change24hUsd = includedWallets.reduce((sum, w) => sum + (w.value_change_24h_usd || 0), 0);
    const change24hPct = totalValue > 0 && includedWallets.length > 0
      ? includedWallets.reduce((sum, w) => {
          const weight = (w.total_value_usd || 0) / totalValue;
          return sum + (w.value_change_24h_percent || 0) * weight;
        }, 0)
      : 0;

    return {
      totalValue,
      change24hUsd,
      change24hPct,
      includedCount: includedWallets.length,
      totalCount: onChainHook.wallets.length,
      // Include individual wallet data for breakdown display
      wallets: includedWallets.map(w => ({
        id: w.id,
        label: w.label || w.wallet_address.slice(0, 6) + '...' + w.wallet_address.slice(-4),
        value: w.total_value_usd || 0,
        change24hUsd: w.value_change_24h_usd || 0,
        change24hPct: w.value_change_24h_percent || 0,
      })),
    };
  }, [onChainHook.wallets]);

  // Build tokens from portfolio data
  const tokens = useMemo(() => {
    if (!portfolioHook.tokens.length && !positionsHook.positions.length) return [];

    const positionsByToken = new Map<string, Position[]>();
    positionsHook.positions.forEach((pos: any) => {
      const tokenId = pos.coin_id || pos.token_id || pos.tokenId;
      const existing = positionsByToken.get(tokenId) || [];
      positionsByToken.set(tokenId, [...existing, {
        id: pos.id,
        type: pos.type,
        date: pos.date,
        quantity: typeof pos.quantity === 'string' ? parseFloat(pos.quantity) : pos.quantity,
        price: typeof pos.price === 'string' ? parseFloat(pos.price) : pos.price,
        note: pos.note || undefined,
      }]);
    });

    return portfolioHook.tokens.map((token: any) => {
      const tokenId = token.coin_id || token.id;
      return {
        id: tokenId,
        displayName: (token.symbol || token.name || tokenId || 'UNKNOWN').toUpperCase(),
        positions: positionsByToken.get(tokenId) || [],
      };
    });
  }, [portfolioHook.tokens, positionsHook.positions]);

  // Fetch prices
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);
  const { data: prices = {}, isLoading: pricesLoading } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000,
  });

  // Load cached prices as fallback
  const [cachedPrices, setCachedPrices] = useState<Prices>({});
  useEffect(() => {
    const cached = StorageService.get<{ prices: Prices }>('cached_prices');
    if (cached?.prices) setCachedPrices(cached.prices);
  }, []);

  const mergedPrices = useMemo(() => {
    if (pricesLoading && Object.keys(cachedPrices).length > 0) {
      return { ...cachedPrices, ...prices };
    }
    return prices;
  }, [prices, cachedPrices, pricesLoading]);

  // Calculate FIFO results
  const fifoResults: FIFOResults = useMemo(() => {
    const results: FIFOResults = {};
    tokens.forEach(token => {
      results[token.id] = CalculatorFactory.calculate(token.positions, calculationMethod);
    });
    return results;
  }, [tokens, calculationMethod]);

  // Calculate portfolio metrics (OffChain + OnChain aggregated)
  const metrics = useMemo(() => {
    let eurUsdRate = 1;
    if (currency === 'EUR') {
      for (const token of tokens) {
        const price = mergedPrices[token.id];
        if (price?.usd && price?.eur) {
          eurUsdRate = price.eur / price.usd;
          break;
        }
      }
    }

    // OffChain portfolio value
    const offChainValue = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      const price = mergedPrices[token.id];
      const currentPrice = currency === 'EUR'
        ? (price?.eur || (price?.usd || 0) * eurUsdRate)
        : (price?.usd || 0);
      return sum + ((fifo.remainingQty || 0) * currentPrice);
    }, 0);

    // Add OnChain value (already in USD, convert if needed)
    const onChainValueConverted = currency === 'EUR'
      ? onChainAggregated.totalValue * eurUsdRate
      : onChainAggregated.totalValue;

    const totalValue = offChainValue + onChainValueConverted;

    const totalCostUsd = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      return sum + (fifo.totalCost || 0);
    }, 0);
    const totalCost = currency === 'EUR' ? totalCostUsd * eurUsdRate : totalCostUsd;

    const totalRealizedPLUsd = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      return sum + (fifo.realizedPL || 0);
    }, 0);
    const totalRealizedPL = currency === 'EUR' ? totalRealizedPLUsd * eurUsdRate : totalRealizedPLUsd;

    const totalUnrealizedPL = totalValue - totalCost;
    const totalPL = totalRealizedPL + totalUnrealizedPL;
    const totalPLPct = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;

    // Find best and worst performer
    let bestPerformer = { symbol: 'N/A', pct: 0, value: 0 };
    let worstPerformer = { symbol: 'N/A', pct: 0, value: 0 };
    tokens.forEach(token => {
      const fifo = fifoResults[token.id];
      const price = mergedPrices[token.id];
      if (fifo && price) {
        const value = (fifo.remainingQty || 0) * (price.usd || 0);
        if (fifo.unrealizedPLPct > bestPerformer.pct) {
          bestPerformer = { symbol: token.displayName, pct: fifo.unrealizedPLPct, value };
        }
        if (fifo.unrealizedPLPct < worstPerformer.pct) {
          worstPerformer = { symbol: token.displayName, pct: fifo.unrealizedPLPct, value };
        }
      }
    });

    // Calculate 24h change (OffChain)
    const offChainChange24h = tokens.reduce((sum, token) => {
      const price = mergedPrices[token.id];
      const fifo = fifoResults[token.id];
      if (price && fifo) {
        const value = (fifo.remainingQty || 0) * (price.usd || 0);
        const changePct = price.usd_24h_change || 0;
        return sum + (value * changePct / 100);
      }
      return sum;
    }, 0);

    // Add OnChain 24h change
    const onChainChange24hConverted = currency === 'EUR'
      ? onChainAggregated.change24hUsd * eurUsdRate
      : onChainAggregated.change24hUsd;

    const change24h = offChainChange24h + onChainChange24hConverted;
    const change24hPct = totalValue > 0 ? (change24h / totalValue) * 100 : 0;

    return {
      totalValue,
      offChainValue,
      onChainValue: onChainValueConverted,
      totalCost,
      totalPL,
      totalPLPct,
      totalRealizedPL,
      totalUnrealizedPL,
      bestPerformer,
      worstPerformer,
      change24h,
      change24hPct,
      tokensCount: tokens.length,
      onChainWalletsCount: onChainAggregated.includedCount,
    };
  }, [tokens, fifoResults, mergedPrices, currency, onChainAggregated]);

  // Generate multiple AI messages for rotation
  const aiMessages = useMemo(() => {
    const messages: { highlight: string; value: string; message: string }[] = [];

    // Message 1: Best performer
    if (metrics.bestPerformer.symbol !== 'N/A' && metrics.bestPerformer.pct !== 0) {
      messages.push({
        highlight: metrics.bestPerformer.symbol,
        value: `${metrics.bestPerformer.pct >= 0 ? '+' : ''}${metrics.bestPerformer.pct.toFixed(0)}%`,
        message: metrics.bestPerformer.pct >= 0 ? t('home.ai.bestPerformerPositive') : t('home.ai.bestPerformerNegative'),
      });
    }

    // Message 2: 24h change
    if (metrics.change24hPct !== 0) {
      messages.push({
        highlight: 'portfolio',
        value: `${metrics.change24hPct >= 0 ? '+' : ''}${metrics.change24hPct.toFixed(1)}%`,
        message: metrics.change24hPct >= 0 ? t('home.ai.portfolioIncreased') : t('home.ai.portfolioDecreased'),
      });
    }

    // Message 3: Diversification
    messages.push({
      highlight: 'diversification',
      value: `${metrics.tokensCount} ${t('home.tokens')}`,
      message: metrics.tokensCount > 5 ? t('home.ai.diversificationGood') : t('home.ai.diversificationNeutral'),
    });

    // Message 4: Total P/L
    if (metrics.totalPL !== 0) {
      messages.push({
        highlight: t('home.totalPL').toLowerCase(),
        value: `${metrics.totalPL >= 0 ? '+' : ''}${currencyInfo.symbol}${fmt(Math.abs(metrics.totalPL), 0)}`,
        message: metrics.totalPL >= 0 ? t('home.ai.profitPositive') : t('home.ai.profitNegative'),
      });
    }

    // Message 5: Worst performer (if negative)
    if (metrics.worstPerformer.symbol !== 'N/A' && metrics.worstPerformer.pct < -10) {
      messages.push({
        highlight: metrics.worstPerformer.symbol,
        value: `${metrics.worstPerformer.pct.toFixed(0)}%`,
        message: t('home.ai.underperforming'),
      });
    }

    // Message 6: Portfolio value milestone
    if (metrics.totalValue > 10000) {
      messages.push({
        highlight: t('home.totalValue').toLowerCase(),
        value: `${currencyInfo.symbol}${fmt(metrics.totalValue, 0)}`,
        message: t('home.ai.portfolioValue'),
      });
    }

    // Message 7: Realized gains
    if (metrics.totalRealizedPL > 0) {
      messages.push({
        highlight: t('home.ai.realizedGains').split(' ')[0].toLowerCase(),
        value: `+${currencyInfo.symbol}${fmt(metrics.totalRealizedPL, 0)}`,
        message: t('home.ai.realizedGains'),
      });
    }

    // Fallback message
    if (messages.length === 0) {
      messages.push({
        highlight: 'Edouard AI',
        value: t('home.ai.is'),
        message: t('home.ai.readyToHelp'),
      });
    }

    return messages;
  }, [metrics, currencyInfo.symbol, t]);

  // Rotate AI messages every 5 seconds
  useEffect(() => {
    if (aiMessages.length <= 1) return;

    const interval = setInterval(() => {
      setAiMessageIndex(prev => (prev + 1) % aiMessages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [aiMessages.length]);

  const currentAiMessage = aiMessages[aiMessageIndex] || aiMessages[0];

  const loading = portfolioHook.loading || positionsHook.loading;

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-page__loading">
          <div className="spinner" />
          <p>{t('home.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Header with greeting */}
      <header className="home-page__header">
        <h1 className="home-page__greeting">{t(getGreetingKey())}, <span>{displayName}</span>.</h1>
        <div className="home-page__header-actions">
          {onChainHook.wallets.length > 0 && (
            <OnChainWalletSelector
              wallets={onChainHook.wallets}
              onToggleWallet={onChainHook.toggleHomepageInclusion}
              isLoading={onChainHook.isLoading}
              totalValueUsd={onChainAggregated.totalValue}
              includedCount={onChainAggregated.includedCount}
            />
          )}
          <button
            className="home-page__ipad-btn"
            onClick={() => onNavigate?.('dashboard-ipad')}
            title={t('home.ipadMode', 'iPad Dashboard')}
          >
            <Icons.Tablet size={16} />
          </button>
        </div>
      </header>

      {/* Service Status Widgets Row */}
      <div className="home-page__widgets-row">
        <FluxNodeStatusWidget onNavigate={onNavigate} />
        <FluxAppsWidget onNavigate={onNavigate} />
        <CronosStakingWidget onNavigate={onNavigate} />
        <HederaStakingWidget onNavigate={onNavigate} />
        <TheGraphWidget onNavigate={onNavigate} />
      </div>

      {/* KPI Cards Row - Glass Cards with Hero Numbers */}
      <div className="home-page__kpi-row">
        {/* Total Value Card */}
        <div
          className="home-page__kpi-card-v2"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.08) 100%)',
            borderColor: 'rgba(139, 92, 246, 0.25)',
          }}
        >
          <div className="home-page__kpi-watermark">
            <Icons.Wallet size={64} />
          </div>
          <div className="home-page__kpi-content">
            <span className="home-page__kpi-label-v2">{t('home.totalValue')}</span>
            <span className="home-page__kpi-hero" style={{ color: '#a78bfa' }}>
              {currencyInfo.symbol}{fmt(metrics.totalValue, 0)}
            </span>
            <div className="home-page__kpi-sub">
              {metrics.onChainValue > 0 ? (
                <>
                  <span className="home-page__kpi-breakdown">
                    <Icons.Briefcase size={12} />
                    <span>{currencyInfo.symbol}{fmt(metrics.offChainValue, 0)}</span>
                  </span>
                  <span className="home-page__kpi-separator">+</span>
                  <span className="home-page__kpi-breakdown home-page__kpi-breakdown--onchain">
                    <Icons.Link size={12} />
                    <span>{currencyInfo.symbol}{fmt(metrics.onChainValue, 0)}</span>
                  </span>
                </>
              ) : (
                <>
                  <Icons.Briefcase size={12} />
                  <span>{metrics.tokensCount} {t('home.tokens')}</span>
                </>
              )}
            </div>
            {/* Wallet breakdown */}
            {onChainAggregated.wallets.length > 0 && (
              <div className="home-page__kpi-wallets">
                {onChainAggregated.wallets.map(w => (
                  <div key={w.id} className="home-page__kpi-wallet-row">
                    <span className="home-page__kpi-wallet-label">{w.label}</span>
                    <span className="home-page__kpi-wallet-value">{currencyInfo.symbol}{fmt(w.value, 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Total P/L Card */}
        <div
          className="home-page__kpi-card-v2"
          style={{
            background: metrics.totalPL >= 0
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(0, 212, 170, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(255, 107, 107, 0.08) 100%)',
            borderColor: metrics.totalPL >= 0 ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
          }}
        >
          <div className="home-page__kpi-watermark" style={{ color: metrics.totalPL >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)' }}>
            {metrics.totalPL >= 0 ? <Icons.TrendingUp size={64} /> : <Icons.TrendingDown size={64} />}
          </div>
          <div className="home-page__kpi-content">
            <span className="home-page__kpi-label-v2">{t('home.totalPL')}</span>
            <span className="home-page__kpi-hero" style={{ color: metrics.totalPL >= 0 ? '#10b981' : '#ef4444' }}>
              {metrics.totalPL >= 0 ? '+' : ''}{currencyInfo.symbol}{fmt(metrics.totalPL, 0)}
            </span>
            <div className="home-page__kpi-sub">
              <div
                className="home-page__kpi-badge"
                style={{
                  background: metrics.totalPL >= 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: metrics.totalPL >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {metrics.totalPL >= 0 ? <Icons.TrendingUp size={12} /> : <Icons.TrendingDown size={12} />}
                {metrics.totalPLPct >= 0 ? '+' : ''}{metrics.totalPLPct.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* 24h Change Card */}
        <div
          className="home-page__kpi-card-v2"
          style={{
            background: metrics.change24hPct >= 0
              ? 'linear-gradient(135deg, rgba(0, 212, 170, 0.15) 0%, rgba(6, 182, 212, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(245, 158, 11, 0.08) 100%)',
            borderColor: metrics.change24hPct >= 0 ? 'rgba(0, 212, 170, 0.25)' : 'rgba(251, 146, 60, 0.25)',
          }}
        >
          <div className="home-page__kpi-watermark" style={{ color: metrics.change24hPct >= 0 ? 'rgba(0, 212, 170, 0.1)' : 'rgba(251, 146, 60, 0.1)' }}>
            <Icons.Clock size={64} />
          </div>
          <div className="home-page__kpi-content">
            <span className="home-page__kpi-label-v2">{t('home.change24h')}</span>
            <span className="home-page__kpi-hero" style={{ color: metrics.change24hPct >= 0 ? '#00d4aa' : '#fb923c' }}>
              {metrics.change24hPct >= 0 ? '+' : ''}{metrics.change24hPct.toFixed(2)}%
            </span>
            <div className="home-page__kpi-sub">
              <div
                className="home-page__kpi-badge"
                style={{
                  background: metrics.change24hPct >= 0 ? 'rgba(0, 212, 170, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                  color: metrics.change24hPct >= 0 ? '#00d4aa' : '#fb923c',
                }}
              >
                {metrics.change24hPct >= 0 ? <Icons.TrendingUp size={12} /> : <Icons.TrendingDown size={12} />}
                {metrics.change24h >= 0 ? '+' : ''}{currencyInfo.symbol}{fmt(Math.abs(metrics.change24h), 0)}
              </div>
            </div>
            {/* Wallet 24h breakdown */}
            {onChainAggregated.wallets.length > 0 && (
              <div className="home-page__kpi-wallets">
                {onChainAggregated.wallets.map(w => (
                  <div key={w.id} className="home-page__kpi-wallet-row">
                    <span className="home-page__kpi-wallet-label">{w.label}</span>
                    <span className={`home-page__kpi-wallet-value ${w.change24hPct >= 0 ? 'positive' : 'negative'}`}>
                      {w.change24hPct >= 0 ? '+' : ''}{w.change24hPct.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Best Performer Card */}
        <div
          className="home-page__kpi-card-v2"
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(234, 179, 8, 0.08) 100%)',
            borderColor: 'rgba(245, 158, 11, 0.25)',
          }}
        >
          <div className="home-page__kpi-watermark" style={{ color: 'rgba(245, 158, 11, 0.1)' }}>
            <Icons.Award size={64} />
          </div>
          <div className="home-page__kpi-content">
            <span className="home-page__kpi-label-v2">{t('home.bestPerformer')}</span>
            <span className="home-page__kpi-hero" style={{ color: '#f59e0b', fontSize: '28px' }}>
              {metrics.bestPerformer.symbol}
            </span>
            <div className="home-page__kpi-sub">
              <div
                className="home-page__kpi-badge"
                style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                }}
              >
                <Icons.TrendingUp size={12} />
                +{metrics.bestPerformer.pct.toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="home-page__simple-layout">
        {/* AI Insights Card - The main hero card */}
        <div className="home-page__ai-card home-page__ai-card--large">
          <div className="home-page__ai-header">
            <Icons.Sparkles size={18} className="home-page__ai-icon" />
            <span>{t('home.generatedByAI')}</span>
            <div className="home-page__ai-dots">
              {aiMessages.map((_, i) => (
                <span
                  key={i}
                  className={`home-page__ai-dot ${i === aiMessageIndex ? 'active' : ''}`}
                  onClick={() => setAiMessageIndex(i)}
                />
              ))}
            </div>
          </div>
          <div className="home-page__ai-content">
            <p className="home-page__ai-message" key={aiMessageIndex}>
              {t('home.ai.yourHighlight')} <span className="highlight-cyan">{currentAiMessage.highlight}</span> {t('home.ai.is')}{' '}
              <span className="highlight-green">{currentAiMessage.value}</span> — {currentAiMessage.message}
            </p>
          </div>
          {/*
            "Try Edouard AI" CTA removed pending real implementation in
            Phase 8 (AI Native). The rotating insights above already show
            real portfolio data — keeping that, removing the false promise.
            See ROADMAP_UNIFIED.md P0 honesty + P8 AI native.
          */}
        </div>

        {/* Quick Actions */}
        <div className="home-page__quick-section home-page__quick-section--expanded">
          <h2>{t('home.quickActions')}</h2>
          <div className="home-page__quick-grid home-page__quick-grid--expanded">
            <button className="home-page__quick-btn home-page__quick-btn--large" onClick={() => onNavigate?.('module-hub')}>
              <div className="home-page__quick-icon home-page__quick-icon--purple"><Icons.Layout size={24} /></div>
              <div className="home-page__quick-text">
                <span className="home-page__quick-title">{t('home.modules')}</span>
                <span className="home-page__quick-subtitle">{t('home.configurePages')}</span>
              </div>
              <Icons.ChevronRight size={20} className="home-page__quick-arrow" />
            </button>
            <button className="home-page__quick-btn home-page__quick-btn--large" onClick={() => onNavigate?.('portfolio')}>
              <div className="home-page__quick-icon home-page__quick-icon--teal"><Icons.Briefcase size={24} /></div>
              <div className="home-page__quick-text">
                <span className="home-page__quick-title">{t('home.portfolioOffchain', 'Portfolio OffChain')}</span>
                <span className="home-page__quick-subtitle">{metrics.tokensCount} {t('home.tokens')} · {currencyInfo.symbol}{fmt(metrics.totalValue, 0)}</span>
              </div>
              <Icons.ChevronRight size={20} className="home-page__quick-arrow" />
            </button>
            <button className="home-page__quick-btn home-page__quick-btn--large" onClick={() => onNavigate?.('portfolio-onchain')}>
              <div className="home-page__quick-icon home-page__quick-icon--blue"><Icons.Link size={24} /></div>
              <div className="home-page__quick-text">
                <span className="home-page__quick-title">{t('home.portfolioOnchain', 'Portfolio OnChain')}</span>
                <span className="home-page__quick-subtitle">{t('home.onchainSubtitle', 'Wallets EVM via DeBank')}</span>
              </div>
              <Icons.ChevronRight size={20} className="home-page__quick-arrow" />
            </button>
            <button className="home-page__quick-btn home-page__quick-btn--large" onClick={() => onNavigate?.('bitcoin-cycles')}>
              <div className="home-page__quick-icon home-page__quick-icon--orange"><Icons.TrendingUp size={24} /></div>
              <div className="home-page__quick-text">
                <span className="home-page__quick-title">{t('home.bitcoinCycles')}</span>
                <span className="home-page__quick-subtitle">{t('home.historicalAnalysis')}</span>
              </div>
              <Icons.ChevronRight size={20} className="home-page__quick-arrow" />
            </button>
            {/* Monitors - 3 in 1 row */}
            <div className="home-page__quick-btn home-page__quick-btn--large home-page__quick-btn--monitors">
              <div className="home-page__quick-text" style={{ flex: 'none', marginRight: '8px' }}>
                <span className="home-page__quick-title">{t('home.monitors')}</span>
                <span className="home-page__quick-subtitle">{t('home.stakingRewards')}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate?.('fluxnode-monitor'); }}
                  className="home-page__quick-mini-btn"
                  title="FluxNode Monitor"
                >
                  <Icons.Server size={18} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate?.('hedera-monitor'); }}
                  className="home-page__quick-mini-btn"
                  title="Hedera Monitor"
                >
                  <Icons.Hash size={18} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate?.('cronos-monitor'); }}
                  className="home-page__quick-mini-btn"
                  title="Cronos Monitor"
                >
                  <Icons.Activity size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OpenClaw on Flux - Promo Block */}
      <div
        className="home-page__openclaw-block"
        onClick={() => onNavigate?.('openclaw-on-flux')}
      >
        <div className="home-page__openclaw-bg" />
        <div className="home-page__openclaw-content">
          <div className="home-page__openclaw-badges">
            <span className="home-page__openclaw-badge home-page__openclaw-badge--ai">
              <Icons.Bot size={12} /> OpenClaw.ai
            </span>
            <span className="home-page__openclaw-badge home-page__openclaw-badge--flux">
              <Icons.Server size={12} /> RunOnFlux
            </span>
          </div>
          <h3 className="home-page__openclaw-title">
            {t('home.openclawTitle', 'Need an AI Assistant?')}
          </h3>
          <p className="home-page__openclaw-desc">
            {t('home.openclawDesc', 'Instead of buying a Mac Mini to run OpenClaw, buy a Stratus node and get your hosting for free — forever!')}
          </p>
          <div className="home-page__openclaw-cta">
            <span className="home-page__openclaw-btn">
              {t('home.openclawLearnMore', 'Learn More')} <Icons.ArrowRight size={14} />
            </span>
          </div>
        </div>
        <div className="home-page__openclaw-visual">
          <div className="home-page__openclaw-icon-stack">
            <div className="home-page__openclaw-icon home-page__openclaw-icon--bot">
              <Icons.Bot size={32} />
            </div>
            <div className="home-page__openclaw-icon home-page__openclaw-icon--plus">
              <Icons.Plus size={16} />
            </div>
            <div className="home-page__openclaw-icon home-page__openclaw-icon--server">
              <Icons.Server size={32} />
            </div>
          </div>
        </div>
      </div>

      {/* Latest Blog Articles */}
      <div className="home-page__blog-section">
        <div className="home-page__blog-header">
          <h3>
            <Icons.FileText size={20} />
            {t('home.latestArticles')}
          </h3>
          <a className="home-page__blog-link" href={`https://edouard.ai/${language === 'fr' ? 'fr' : 'en'}/blog`} target="_blank" rel="noopener noreferrer">
            {t('home.viewAll')} <Icons.ArrowRight size={14} />
          </a>
        </div>
        <div className="home-page__blog-grid">
          {blogLoading ? (
            // Skeleton loading
            <>
              {[1, 2, 3].map(i => (
                <div key={i} className="home-page__blog-card home-page__blog-card--skeleton">
                  <div className="home-page__blog-image-skeleton" />
                  <div className="home-page__blog-content">
                    <div className="home-page__blog-title-skeleton" />
                    <div className="home-page__blog-excerpt-skeleton" />
                    <div className="home-page__blog-meta-skeleton" />
                  </div>
                </div>
              ))}
            </>
          ) : blogArticles.length > 0 ? (
            blogArticles.map(article => {
              const showPlaceholder = !hasValidCover(article.cover_image) || blogImageErrors.has(article.id);
              return (
                <a
                  key={article.id}
                  href={`https://edouard.ai/${language === 'fr' ? 'fr' : 'en'}/blog/${article.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-page__blog-card"
                >
                  {/* Cover image or placeholder */}
                  <div className="home-page__blog-image">
                    {showPlaceholder ? (
                      <div
                        className="home-page__blog-placeholder"
                        style={{
                          background: `linear-gradient(135deg, ${article.category?.color || '#8b5cf6'}20 0%, ${article.category?.color || '#8b5cf6'}30 50%, ${article.category?.color || '#8b5cf6'}15 100%)`,
                        }}
                      >
                        <div className="home-page__blog-placeholder-icon" style={{ backgroundColor: `${article.category?.color || '#8b5cf6'}20` }}>
                          <Icons.FileText size={24} style={{ color: article.category?.color || '#8b5cf6' }} />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={article.cover_image!}
                        alt={article.title}
                        onError={() => setBlogImageErrors(prev => new Set(prev).add(article.id))}
                      />
                    )}
                    {/* Category badge */}
                    {article.category && (
                      <span
                        className="home-page__blog-category"
                        style={{ backgroundColor: article.category.color || '#8b5cf6' }}
                      >
                        {article.category.name}
                      </span>
                    )}
                  </div>
                  <div className="home-page__blog-content">
                    <h4 className="home-page__blog-title">{article.title}</h4>
                    {article.excerpt && (
                      <p className="home-page__blog-excerpt">{article.excerpt}</p>
                    )}
                    <div className="home-page__blog-meta">
                      <span className="home-page__blog-date">
                        <Icons.Calendar size={12} />
                        {formatBlogDate(article.published_at)}
                      </span>
                      <span className="home-page__blog-reading-time">
                        <Icons.Clock size={12} />
                        {article.reading_time} min
                      </span>
                    </div>
                  </div>
                </a>
              );
            })
          ) : (
            <div className="home-page__blog-empty">
              <Icons.FileText size={24} />
              <p>{t('home.noArticles')}</p>
            </div>
          )}
          {/* Discrete Quiz Owl - appears at the end of articles */}
          <QuizOwl onClick={() => onNavigate?.('crypto-quiz')} />
        </div>
      </div>
    </div>
  );
}

export default HomePage;
